<template>
  <b-card
    :title="title"
    :hideTitle="mode === 'readonly'"
    :hideDefaultFooter="mode === 'readonly'"
    @cancel="closeModal()"
    @submit="submitCustomer"
    :error-message="errorMessage"
    :loading="loading || isLoadingCustomer"
    :card-loading="isLoadingCustomer"
    :disabled="mode === 'create' ? !formValid : false"
    :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
    :cancel-text="{ el: 'Ακύρωση', en: 'Cancel', it: 'Cancella' }"
  >
    <v-fade-transition>
      <div v-if="isLoadingCustomer || loading" key="skeleton-loader">
        <v-row no-gutters>
          <v-col cols="12" class="mb-3 py-4">
            <v-skeleton-loader type="heading" class="mx-auto"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" class="mb-3 py-4">
            <v-skeleton-loader type="heading" class="mx-auto"></v-skeleton-loader>
          </v-col>

          <v-col cols="12" class="mb-3 py-4">
            <v-skeleton-loader type="heading" class="mx-auto"></v-skeleton-loader>
          </v-col>

          <v-col cols="12" sm="6" class="px-4 mb-3 py-2" v-for="i in 4" :key="`row1-${i}`">
            <v-skeleton-loader type="text" class="mx-auto"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" sm="6" class="px-4 mb-3 py-2" v-for="i in 4" :key="`row1-right-${i}`">
            <v-skeleton-loader type="text" class="mx-auto"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" class="mb-3 py-4">
            <v-skeleton-loader type="heading" class="mx-auto"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" class="mb-3 py-4">
            <v-skeleton-loader type="heading" class="mx-auto"></v-skeleton-loader>
          </v-col>

          <v-col cols="12" sm="6" class="px-4 mb-3 py-2" v-for="i in 6" :key="`row1-${i}`">
            <v-skeleton-loader type="text" class="mx-auto"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" sm="6" class="px-4 mb-3 py-2" v-for="i in 6" :key="`row1-right-${i}`">
            <v-skeleton-loader type="text" class="mx-auto"></v-skeleton-loader>
          </v-col>
        </v-row>
      </div>
    </v-fade-transition>

    <v-row v-show="!(isLoadingCustomer || loading)" no-gutters>
      <v-col cols="12" v-if="mode == 'update'">
        <v-sheet
          color="rgba(234, 237, 241, 0.57)"
          outlined
          class="pa-3 mb-3 d-flex justify-space-between"
        >
          <div>
            {{ translations.cardNumbers[lang] }}
            {{ customerData.card_numbers ? customerData.card_numbers.toString() : '-' }}
          </div>
          <v-btn @click="$emit('openAddCardDialog', customerData)">{{
            translations.addCard[lang]
          }}</v-btn>
        </v-sheet>
      </v-col>

      <v-col cols="12" v-if="mode == 'update'">
        <v-sheet
          color="rgba(234, 237, 241, 0.57)"
          outlined
          class="pa-3 mb-3 d-flex justify-space-between"
        >
          <div>
            {{ translations.registerFrom[lang] }}
            {{ customerData.source ? customerData.source : '-' }}
          </div>
        </v-sheet>
      </v-col>

      <v-col cols="4" sm="4" class="pr-2 pb-3">
        <b-select
          class="country-input"
          :readonly="mode == 'readonly'"
          :items="activeCountries"
          item-value="country_id"
          item-text="name"
          :label="translations.cc[lang]"
          hint="Country's dial code"
          v-model="customerData.country_id"
          :success="success.country"
          :error-messages="error.country"
          :disabled="mode == 'update'"
          @focus="error.country = ''"
          @blur="validateCountry"
        ></b-select>
      </v-col>

      <v-col cols="8" sm="8" class="pl-sm-2">
        <b-text-field
          class="mobile-input"
          :readonly="mode == 'readonly'"
          :label="translations.mobile[lang]"
          hint="ex: 6912345780"
          v-model="customerData.mobile"
          :success="success.mobile"
          :error-messages="error.mobile"
          :disabled="mode == 'update'"
          @focus="error.mobile = ''"
          @blur="validateMobile"
        ></b-text-field>
      </v-col>

      <v-col cols="12">
        <v-menu
          :close-on-content-click="true"
          offset-y
          :disabled="mode == 'readonly'"
          max-width="290px"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              :label="translations.formDate[lang]"
              color="secondary"
              autocomplete="off"
              hint="YYYY/DD format"
              :readonly="mode == 'readonly'"
              outlined
              dense
              :clearable="mode != 'readonly'"
              hide-details
              v-on="on"
              v-model="customerData.form_date"
            ></v-text-field>
          </template>

          <v-date-picker
            color="secondary"
            no-title
            v-model="customerData.form_date"
            :max="new Date().toISOString().substr(0, 10)"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col
        cols="12"
        sm="7"
        md="3"
        lg="2"
        xl="3"
        class="pr-0"
        v-if="mode == 'create'"
        style="min-width: 124px"
      >
        <b-text-field
          :readonly="mode == 'readonly'"
          :label="translations.cardNumber[lang]"
          v-model="customerData.card_number"
          no-margin-top
          :clearable="false"
          :success="success.card_number"
          :error-messages="error.card_number"
          @focus="error.card_number = ''"
          @blur="validateStore"
        ></b-text-field>
      </v-col>

      <v-col cols="12">
        <v-menu
          v-model="storeMenu"
          :disabled="mode == 'readonly'"
          max-height="300"
          offset-y
          :close-on-content-click="false"
          nudge-bottom="3"
        >
          <template v-slot:activator="{ on }">
            <v-text-field
              v-model="search"
              ref="search"
              :label="translations.store[lang]"
              color="secondary"
              outlined
              dense
              :clearable="mode != 'readonly'"
              v-on="on"
              :readonly="mode == 'readonly'"
              hide-details="auto"
              autocomplete="off"
              class="mt-3"
              :class="{ 'pa-0': mode == 'update' }"
              :success="success.store"
              :error-messages="error.store"
              @focus="error.store = ''"
              @blur="validateStore"
              :append-icon="storeMenu ? icons.mdiMenuUp : icons.mdiMenuDown"
              :loading="companyStoresLoading"
              @click:append="
                () => {
                  storeMenu = !storeMenu && mode != 'readonly';
                  $refs.search.focus();
                }
              "
            ></v-text-field>
          </template>

          <v-list dense>
            <template v-if="selectableCompanyStores.length">
              <v-list-item
                v-for="(store, index) in selectableCompanyStores"
                :key="index"
                class="pl-3"
                @click="
                  () => {
                    customerData.store_id = store.store_id;
                    search = store.name;
                    storeMenu = false;
                    validateStore();
                  }
                "
              >
                <v-list-item-title>
                  <v-row no-gutters justify="space-between">
                    <v-col cols="auto">
                      {{ store.name }}
                    </v-col>
                  </v-row>
                </v-list-item-title>
              </v-list-item>

              <v-list-item class="pl-3" v-if="storesTotalPages > page">
                <v-list-item-title class="font-weight-black">
                  <v-btn @click="loadMoreStores()"> Load More Stores... </v-btn>
                </v-list-item-title>
              </v-list-item>
            </template>
            <v-list-item v-else>
              <v-list-item-title v-text="'No Data'"></v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <v-col cols="6">
        <b-text-field
          :readonly="mode == 'readonly'"
          :label="translations.firstName[lang]"
          v-model="customerData.first_name"
        ></b-text-field>
      </v-col>

      <v-col cols="6" class="pr-md-2 pl-2">
        <b-text-field
          :readonly="mode == 'readonly'"
          :label="translations.lastName[lang]"
          v-model="customerData.last_name"
        ></b-text-field>
      </v-col>

      <v-col cols="6">
        <b-select
          :readonly="mode == 'readonly'"
          :items="genders"
          item-text="name"
          item-value="value"
          v-model="customerData.gender"
          :label="translations.gender[lang]"
        ></b-select>
      </v-col>

      <v-col cols="6" class="pr-md-2 pl-2">
        <b-text-field
          :readonly="mode == 'readonly'"
          :label="translations.landlinePhone[lang]"
          v-model="customerData.phone"
          hint="ex: 2103456789"
        ></b-text-field>
      </v-col>

      <v-col cols="8" class="pr-2">
        <b-text-field
          :readonly="mode == 'readonly'"
          label="Email"
          hint="ex: customer@mail.com"
          v-model="customerData.email"
        ></b-text-field>
      </v-col>

      <v-col cols="4" class="mt-3">
        <v-menu
          :close-on-content-click="false"
          offset-y
          :disabled="mode == 'readonly'"
          max-width="290px"
          ref="menu"
          v-model="menu"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :readonly="mode == 'readonly'"
              :label="translations.birthDate[lang]"
              color="secondary"
              autocomplete="off"
              hint="YYYY/DD format"
              outlined
              dense
              :clearable="mode != 'readonly'"
              v-bind="attrs"
              hide-details
              v-on="on"
              :value="
                customerData.birth_date ? moment(customerData.birth_date).format('DD-MM-YYYY') : ''
              "
            ></v-text-field>
          </template>

          <v-date-picker
            :readonly="mode == 'readonly'"
            color="secondary"
            no-title
            v-model="customerData.birth_date"
            ref="picker"
            :active-picker.sync="activePicker"
            :locale="locale"
            :max="new Date().toISOString().substring(0, 10)"
            :picker-date="pickerDate"
            min="1930-01-01"
            @change="save"
          ></v-date-picker>
        </v-menu>
      </v-col>

      <v-col cols="4" class="pr-2">
        <b-text-field
          :readonly="mode == 'readonly'"
          :label="translations.postCode[lang]"
          v-model="customerData.zipcode"
        ></b-text-field>
      </v-col>

      <v-col cols="8">
        <Places
          :initial-address="customerData.address"
          :label="translations.address[lang]"
          :readonly="mode == 'readonly'"
          class="mt-3 mb-0"
          outlined
          dense
          hide-details="auto"
          @newAddress="setAddressDetails"
          :clearable="false"
          hint="The customer's residential address"
          ref="places"
        />
      </v-col>

      <v-col cols="12">
        <b-select
          :readonly="mode == 'readonly'"
          :items="counties"
          item-text="name"
          item-value="county_id"
          :label="translations.country[lang]"
          v-model="customerData.county_id"
        ></b-select>
      </v-col>
      <v-col class="px-0 d-flex flex-row">
        <v-col cols="1" class="notification-checkbox-cont d-flex align-center py-2 px-0">
          <v-checkbox
            :readonly="mode == 'readonly'"
            hide-details
            color="black"
            class="marketing-checkbox shrink mr-2 mt-0 mt-1"
            v-model="customerData.marketing"
          ></v-checkbox>
        </v-col>

        <v-col cols="11" class="px-0">
          <v-select
            v-model="selectedNotifications"
            class="marketing-select-notifications"
            :readonly="mode == 'readonly'"
            :items="systemNotifications"
            :label="translations.receiveNews[lang]"
            item-text="displayName"
            multiple
            hide-details
            chips
            deletable-chips
            :menu-props="{
              top: true,
              offsetY: true,
            }"
            :hint="(mode != 'readonly' && translations.selectOneOrMore[lang]) || undefined"
            :disabled="!customerData.marketing"
            @click="notificationsMenu = mode != 'readonly'"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="toggleSelectAllNotifications">
                <v-list-item-action>
                  <v-icon :color="selectedNotifications.length > 0 ? 'indigo darken-4' : ''">
                    {{ notificationSelectIcon }}
                  </v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    <v-row no-gutters>
                      <v-col cols="6">
                        {{ selectAll }}
                      </v-col>
                      <v-col cols="6" align="right">
                        <v-icon
                          color="'indigo darken-4'"
                          @click.prevent.self.stop="notificationsMenu = false"
                        >
                          mdi-close-box
                        </v-icon>
                      </v-col>
                    </v-row>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
            </template>
          </v-select>
        </v-col>
      </v-col>
    </v-row>

    <v-row no-gutters v-if="activeAttributes && activeAttributes.length">
      <v-col cols="12">
        <span class="text-h6 pa-3">{{ translations.customAttributes[lang] }}</span>
      </v-col>
      <v-col
        v-for="attribute in activeAttributes"
        :key="attribute.user_account_attribute_definition_id"
        cols="6"
        class="pa-2"
      >
        <div>
          <v-menu
            v-if="attribute.attribute_type == 'text' && attribute.value_type == 'date'"
            :close-on-content-click="false"
            offset-y
            :disabled="mode == 'readonly'"
            max-width="290px"
            ref="menu"
            v-model="attribute.dateMenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :readonly="mode == 'readonly'"
                :label="attribute.name"
                color="secondary"
                autocomplete="off"
                outlined
                dense
                hide-details
                v-bind="attrs"
                v-on="on"
                :value="attribute.values[0]"
                clearable
                @click:clear="attribute.values[0] = ''; attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values, true)"
              ></v-text-field>
            </template>
            <v-date-picker
              color="secondary"
              no-title
              v-model="attribute.values[0]"
              @input="attribute.dateMenu = false; attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values, true)"
            ></v-date-picker>
          </v-menu>
          <v-menu
            v-else-if="attribute.attribute_type == 'text' && attribute.value_type == 'datetime'"
            :close-on-content-click="false"
            offset-y
            :disabled="mode == 'readonly'"
            max-width="290px"
            ref="menu"
            v-model="attribute.dateMenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :readonly="mode == 'readonly'"
                :label="attribute.name"
                color="secondary"
                autocomplete="off"
                outlined
                dense
                hide-details
                v-bind="attrs"
                v-on="on"
                :value="attribute.values[0]"
                clearable
                @click:clear="attribute.values[0] = ''; attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values, true)"
              ></v-text-field>
            </template>
            <v-date-picker
              color="secondary"
              no-title
              v-model="attribute.dateVal"
              @input="attribute.timeMenu = true"
            ></v-date-picker>
            <v-time-picker
              v-if="attribute.timeMenu"
              color="secondary"
              format="24hr"
              use-seconds
              v-model="attribute.timeVal"
              @click:second="
                attribute.values[0] = (attribute.dateVal || '') + ' ' + (attribute.timeVal || '');
                attribute.dateMenu = false;
                attribute.timeMenu = false;
                attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values, true);
              "
            ></v-time-picker>
          </v-menu>
          <b-text-field
            v-else-if="attribute.attribute_type == 'text'"
            :readonly="mode == 'readonly'"
            :label="attribute.name"
            v-model="attribute.values[0]"
            no-margin-top
            :error-messages="
              attribute.values[0] && attribute.value_type == 'number' && isNaN(attribute.values[0])
                ? translations.invalidValue[lang]
                : ''
            "
            @input="
              attachExtraAttribute(
                attribute.user_account_attribute_definition_id,
                attribute.values,
                true,
                attribute.values[0] &&
                  attribute.value_type == 'number' &&
                  isNaN(attribute.values[0])
              )
            "
          ></b-text-field>
          <b-select
            v-if="attribute.attribute_type == 'select'"
            :readonly="mode == 'readonly'"
            :items="attribute.user_account_attribute_definition_values"
            :label="attribute.name"
            item-text="value"
            item-value="user_account_attribute_definition_value_id"
            v-model="attribute.values[0]"
            @change="
              attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values)
            "
          ></b-select>
          <b-select
            v-if="attribute.attribute_type == 'multi_select'"
            :readonly="mode == 'readonly'"
            :items="attribute.user_account_attribute_definition_values"
            :label="attribute.name"
            multiple
            item-text="value"
            item-value="user_account_attribute_definition_value_id"
            v-model="attribute.values"
            @change="
              attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values)
            "
          ></b-select>
          <v-radio-group
            v-if="attribute.attribute_type == 'radio'"
            :readonly="mode == 'readonly'"
            class="mt-2 pt-0"
            hide-details
            v-model="attribute.values[0]"
            @change="
              attachExtraAttribute(attribute.user_account_attribute_definition_id, attribute.values)
            "
          >
            <v-row no-gutters class="d-flex justify-center">
              <v-col cols="12" md="12" class="d-flex justify-start pa-1"
                ><span class="text-body-2">{{ attribute.name }}:</span></v-col
              >
              <v-col
                v-for="option in attribute.user_account_attribute_definition_values"
                :key="option.user_account_attribute_definition_value_id"
                cols="auto"
                class="pr-2 mr-2"
              >
                <v-radio
                  :readonly="mode == 'readonly'"
                  color="secondary"
                  :value="option.user_account_attribute_definition_value_id"
                  :label="option.value"
                >
                </v-radio>
              </v-col>
            </v-row>
          </v-radio-group>
        </div>
      </v-col>
    </v-row>
  </b-card>
</template>

<script>
import { mdiPlus, mdiCalendarSearch, mdiMenuUp, mdiMenuDown } from '@mdi/js';
import { mapMutations, mapState, mapActions, mapGetters } from 'vuex';
import validators from '@/utils/validators/loyaltyPanel/customers';
import debounce from 'lodash/debounce';
import Places from '@/components/general/Places.vue';
import translations from '@/utils/translations/loyaltyPanel/customers';
import { cloneDeep } from 'lodash';
import moment from 'moment';

export default {
  name: 'CustomerForm',

  mixins: [validators, translations],

  components: { Places },

  props: {
    mode: String,

    dialog: Boolean,
  },

  data() {
    return {
      icons: {
        mdiPlus,
        mdiCalendarSearch,
        mdiMenuUp,
        mdiMenuDown,
      },
      genders: [
        { name: 'Male', value: 'male' },
        { name: 'Female', value: 'female' },
        { name: 'Other', value: 'other' },
      ],
      pickerDate: '1995-1-1',
      menu: false,
      notificationsMenu: false,
      selectAll: '',
      selectedNotifications: [],
      search: '',
      storeMenu: false,
      page: 1,

      activeAttributes: [],
      customerAttributes: [],

      activePicker: null,
      moment,
      renderComponent: true,
      initialAttrs: [],
      notificationMap: {
        mail: 'Email',
        push: 'Push Notification',
        sms: 'SMS/Viber',
        viber: 'Viber',
      },
      componentKey: 0,
      isLoadingCustomer: false,
    };
  },
  computed: {
    ...mapState(['loading', 'errorMessage']),
    ...mapState('loyaltyPanel/customers', [
      'successCode',
      'companyStores',
      'companyStoresLoading',
      'storesTotalPages',
      'attributes',
    ]),
    ...mapGetters(['activeCountries', 'systemNotifications']),

    counties() {
      return this.activeCountries.length && this.customerData.country_id
        ? this.activeCountries.find(
            (country) => country.country_id === this.customerData.country_id
          ).counties
        : [];
    },

    selectableCompanyStores() {
      return (
        this.companyStores?.filter(
          (x) => !x.is_deleted || x.store_id === this.customerData.store_id
        ) || []
      );
    },

    customerData: {
      get() {
        return this.$store.state.loyaltyPanel.customers.customerData;
      },
      set(val) {
        this.setCustomerData(val);
      },
    },

    notificationSelectIcon() {
      if (this.selectedNotifications.length === this.systemNotifications.length)
        return 'mdi-close-box';
      if (
        this.selectedNotifications.length < this.systemNotifications.length &&
        this.selectedNotifications.length > 0
      )
        return 'mdi-minus-box';
      return 'mdi-checkbox-blank-outline';
    },

    lang() {
      return this.$route.params.lang;
    },
    locale() {
      if (this.lang == 'el') return 'el-GR';
      if (this.lang == 'it') return 'it-IT';
      return 'en-GB';
    },
    title() {
      let txt = '';
      if (this.mode === 'create') {
        txt = this.translations.addNewCustomer[this.lang];
      }
      if (this.mode === 'update') {
        txt = this.translations.updateCustomer[this.lang];
      }
      return txt;
    },

    query() {
      let query = `?page=${this.page}`;

      if (this.search) {
        query += `&q=${this.search}`;
      }

      return query;
    },
    userAccountId() {
      return this.customerData.user_account_id;
    },

    customerStore() {
      return this.companyStores.find((x) => x.store_id == this.customerData.store_id) || {};
    },
  },
  watch: {
    menu(val) {
      val && setTimeout(() => ((this.activePicker = 'YEAR'), (this.pickerDate = null)));
    },

    search(val) {
      if (val) {
        this.storeMenu = this.mode != 'readonly';
        if (
          (val == this.customerStore.name &&
            this.customerData?.store_id == this.customerStore.store_id) ||
          (val && this.companyStores.find((x) => x.name == val))
        ) {
          this.storeMenu = false;
        } else {
          this.debouncedSearch();
        }
      } else {
        this.loadStores({
          query: this.query,
          page: this.page,
        });
        this.customerData.store_id = null;
      }
    },

    successCode(val) {
      if (val) {
        this.selectedNotifications = [];
        this.selectAll = this.translations.selectAll[this.lang];
        this.search = '';
      }
    },

    ['userAccountId']: {
      immediate: true,
      async handler(val) {
        this.isLoadingCustomer = true;
        this.customerAttributes = [];
        this.search = '';
        this.setCompanyStores([]);
        if (this.customerData.store_id) {
          if (!this.customerStore?.store_id) {
            await this.loadStores({
              query: `?store=${this.customerData.store_id}&deleted=true`,
            });
          }
          this.search = this.customerStore ? this.customerStore.name : '';
        }

        this.storeMenu = false;
        if (!this.customerData.extra_attributes?.length) {
          this.attributes.forEach((e) => {
            e.values = [];
          });
          this.activeAttributes.forEach((e) => {
            e.values = [];
          });
        }

        this.selectedNotifications = this.customerData.notifications
          ? Object.entries(this.customerData.notifications)
              .map(([k, v], i) => {
                if (v) {
                  return k == 'sms' || k == 'viber' || k == 'sms/viber'
                    ? 'SMS/Viber'
                    : k == 'mail'
                      ? 'Email'
                      : 'Push Notification';
                } else {
                  return false;
                }
              })
              .filter((o) => o)
          : [];
        if (this.selectedNotifications.length === 0) {
          this.customerData.marketing = false;
        }
        this.$refs.places?.clearInput();
        this.$nextTick(() => {
          this.isLoadingCustomer = false;
        });
      },
    },

    selectedNotifications: {
      handler(val) {
        if (val.length) {
          if (!this.customerData.marketing) {
            this.customerData.marketing = true;
          }
          this.setCustomerNotifications();
        } else {
          this.setFalseCustomerNotifications();
          this.customerData.marketing = false;
        }

        if (val.length !== this.systemNotifications.length) {
          this.selectAll = this.translations.selectAll[this.lang];
        } else {
          this.selectAll = this.translations.deselectAll[this.lang];
        }
      },
      deep: true,
    },

    'customerData.marketing': {
      handler(newVal, oldVal) {
        if (this.isLoadingCustomer) {
          return;
        }

        if (oldVal === true && newVal === false) {
          this.selectedNotifications = [];
          this.setFalseCustomerNotifications();
          this.notificationsMenu = false;
        }

        if (oldVal === false && newVal === true) {
          if (this.selectedNotifications.length === 0) {
            this.selectedNotifications = this.systemNotifications.slice().map((v) => v.displayName);
          }
          this.notificationsMenu = !this.notificationsMenu;
        }
      },
    },

    customerAttributes: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val?.length && this.activeAttributes?.length) {
          val.forEach((e, i) => {
            let attr = this.activeAttributes.find(
              (x) =>
                e.user_account_attribute_definition_id == x.user_account_attribute_definition_id
            );
            let defValue = attr.user_account_attribute_definition_values.find(
              (x) =>
                x.user_account_attribute_definition_value_id ==
                e.user_account_attribute_definition_value_id
            );
            e.value = defValue?.value || e.value || null;
            e.user_account_attribute_definition_value_id =
              e.user_account_attribute_definition_value_id ||
              defValue?.user_account_attribute_definition_value_id ||
              null;
            if (
              (attr.attribute_type == 'text' ||
                (e.value && !e.user_account_attribute_definition_value_id)) &&
              !attr.values?.includes(e.value)
            ) {
              attr.values.push(e.value);
            } else if (
              attr.attribute_type != 'text' &&
              e.user_account_attribute_definition_value_id &&
              !attr.values?.includes(e.user_account_attribute_definition_value_id)
            ) {
              attr.values.push(e.user_account_attribute_definition_value_id);
            }
          });
        } else if (this.activeAttributes?.length) {
          this.activeAttributes.forEach((e, i) => {
            e.values = [];
          });
        }
      },
    },

    'customerData.extra_attributes': {
      immediate: true,
      handler(val) {
        this.$nextTick(() => {
          if (val?.length) {
            this.initialAttrs = [];
            val.forEach((e, i) => {
              if (
                ![null, undefined].includes(
                  e.selected_user_account_attribute_definition_values
                    ?.user_account_attribute_definition_value
                )
              ) {
                this.initialAttrs.push({
                  user_account_attribute_definition_id: e.user_account_attribute_definition_id,
                  value:
                    e.selected_user_account_attribute_definition_values
                      ?.user_account_attribute_definition_value || '',
                  user_account_attribute_definition_value_id:
                    e.selected_user_account_attribute_definition_values
                      ?.user_account_attribute_definition_value_id || null,
                });
              }
            });
          } else {
            this.initialAttrs = [];
          }
        });
      },
      deep: true,
    },
    dialog(val) {
      if (!val) {
        this.closeModal();
      } else {
        this.notificationsMenu = false;
      }
    },
    initialAttrs: {
      immediate: true,
      handler(val) {
        if (val?.length && this.customerData.user_account_id) {
          this.customerAttributes = cloneDeep(val);
        }
      },
      deep: true,
    },
    attributes: {
      immediate: true,
      handler(val) {
        this.activeAttributes = [];
        if (val?.length) {
          val.forEach((e) => {
            if (e.active) {
              this.activeAttributes.push({
                ...e,
                values: e.values || [],
                dateMenu: false,
                timeMenu: false,
                dateVal: null,
                timeVal: null,
              });
            }
          });
        }
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions(['loadActiveCountries', 'loadSystemNotifications']),
    ...mapActions('loyaltyPanel/customers', [
      'createNewCustomer',
      'updateCustomer',
      'loadStores',
      'getAttributes',
    ]),
    ...mapMutations('loyaltyPanel/customers', [
      'setCustomerData',
      'setCompanyStores',
      'setAttributes',
    ]),
    ...mapMutations(['setErrorMessage']),

    async submitCustomer() {
      // Prepare notifications for backend
      let notifications = { ...this.customerData.notifications };

      if (this.selectedNotifications.includes('SMS/Viber')) {
        notifications.sms = true;
        notifications.viber = true;
      } else {
        notifications.sms = false;
        notifications.viber = false;
      }
      delete notifications['sms/viber'];

      this.customerData.notifications = notifications;

      this.customerData.extra_attributes = this.customerAttributes;
      let res =
        this.mode === 'create' ? await this.createNewCustomer() : await this.updateCustomer();
      if (res && !res.error) {
        this.closeModal();
      }
    },
    save(date) {
      this.$refs.menu?.save(date);
      this.pickerDate = date;
    },

    setAddressDetails(place) {
      this.customerData.address = place.address;
    },

    closeModal() {
      this.selectedNotifications = [];
      this.selectAll = this.translations.selectAll[this.lang];
      this.search = '';
      this.storeMenu = false;
      this.notificationsMenu = false;
      this.setErrorMessage('');
      this.customerAttributes = [];
      this.activeAttributes = [];
      this.initialAttrs = [];
      this.setCustomerData({});
      this.$emit('close');
    },

    updateNotificationsOnCustomerDetails() {
      for (let selectedNotification of this.selectedNotifications) {
        this.customerData.notifications[selectedNotification] = true;
      }
    },

    loadMoreStores() {
      this.page++;
      this.loadStores({
        query: this.query,
        page: this.page,
      });
    },

    toggleSelectAllNotifications() {
      this.$nextTick(() => {
        if (this.selectedNotifications.length === this.systemNotifications.length) {
          this.selectedNotifications = [];
          this.selectAll = this.translations.selectAll[this.lang];
        } else {
          this.selectedNotifications = this.systemNotifications.slice().map((v) => v.displayName);
          this.selectAll = this.translations.deselectAll[this.lang];
        }
      });
    },

    handleSearch() {
      this.loadStores({
        query: `?q=${this.search}`,
        page: this.page,
      });
    },
    async forceRerender() {
      this.renderComponent = false;
      await this.$nextTick();
      this.renderComponent = true;
    },
    attachExtraAttribute(id, vals, isText, hasError) {
      if (!this.customerData?.extra_attributes?.length) {
        this.customerData.extra_attributes = [];
      }
      let customerAttribute = this.customerAttributes.find(
        (e) => e.user_account_attribute_definition_id == id
      ) || { user_account_attribute_definition_id: id };
      this.customerAttributes = this.customerAttributes.filter(
        (e) => e.user_account_attribute_definition_id != id
      );

      vals.forEach((val) => {
        if (val && !hasError) {
          if (isText) {
            customerAttribute.value = val;
            customerAttribute.user_account_attribute_definition_value_id = null;
          } else {
            customerAttribute.user_account_attribute_definition_value_id = val;
          }
          this.customerAttributes.push({ ...customerAttribute });
        }
      });
    },
    setCustomerNotifications() {
      Object.keys(this.customerData.notifications).forEach((key) => {
        if (this.notificationMap[key]) {
          this.customerData.notifications[key] = this.selectedNotifications.includes(
            this.notificationMap[key]
          );
        }
      });
    },
    setFalseCustomerNotifications() {
      Object.keys(this.customerData.notifications).forEach((key) => {
        this.customerData.notifications[key] = false;
      });
    },
  },
  async mounted() {
    this.loadActiveCountries();

    if (
      !this.attributes?.length ||
      this.attributes[0]?.store_id !== Number(localStorage.getItem('storeId'))
    ) {
      this.setAttributes([]);
      this.getAttributes();
    }
    await this.loadSystemNotifications();
    this.selectAll = this.translations.selectAll[this.lang];

    // Replace sms/viber with sms on customerData.notifications and delete the sms/viber
    this.customerData.notifications.sms = this.customerData.notifications['sms/viber'];
    delete this.customerData.notifications['sms/viber'];

    this.selectedNotifications = this.customerData.notifications
      ? Object.entries(this.customerData.notifications)
          .map(([k, v], i) => {
            if (v) {
              return k == 'sms' || k == 'viber' || k == 'sms/viber'
                ? 'SMS/Viber'
                : k == 'mail'
                  ? 'Email'
                  : 'Push Notification';
            } else {
              return false;
            }
          })
          .filter((o) => o)
      : [];
    if (this.selectedNotifications.length === this.systemNotifications.length) {
      this.selectAll = this.translations.deselectAll[this.lang];
    }
  },
  created() {
    this.debouncedSearch = debounce(this.handleSearch, 500);
  },
};
</script>

<style>
.card-cvc-field .v-messages__wrapper {
  position: absolute;
  top: 1px;
  overflow: visible;
  min-width: 150px;
  left: -11px;
}
.card-cvc-field .v-text-field__details {
  overflow: visible;
}

.notification-checkbox-cont {
  max-width: 48px;
}

.marketing-select-notifications .v-label--is-disabled {
  color: #000 !important;
}
.marketing-select-notifications.v-input--is-disabled .v-input__slot::before {
  display: none;
}

.country-input.v-input--is-disabled .v-select__selection,
.country-input.v-input--is-disabled input,
.mobile-input.v-input--is-disabled input {
  color: #000 !important;
  -webkit-text-fill-color: #000 !important;
}
</style>
